/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印使用引导（使用 react-joyride）
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-28 13:59:50
 */

import React, { useState, useCallback, useEffect } from 'react'
import Joyride, { Step, CallBackProps, STATUS, ACTIONS } from 'react-joyride'

import './UsageGuide.scss'

interface UsageGuideProps {
  run: boolean
  onComplete?: () => void
  onSkip?: () => void
  onFinish?: () => void
}

// 本地缓存 key
const GUIDE_SHOWN_KEY = 'watermark-batch-editor-guide-shown'

const UsageGuide: React.FC<UsageGuideProps> = ({
  run,
  onComplete,
  onSkip,
  onFinish
}) => {
  const [stepIndex, setStepIndex] = useState(0)

  const steps: Step[] = [
    {
      target: '.watermarkBatchEditor .leftPanel',
      content: (
        <div className="joyride-step-content joyride-step-add-photo">
          <div className="joyride-step-header">
            <div className="joyride-step-number">1</div>
            <h3 className="joyride-step-title">欢迎使用批量编辑！👋</h3>
          </div>
          <div>
            <p className="joyride-step-description">
              您可以先在照片列表中
              <strong className="highlight-text">点击一张照片</strong>
              ，我们会自动帮您把水印信息找出来，作为编辑的模板。
            </p>
            <p className="joyride-step-description joyride-step-hint">
              ✨ 想一次改更多照片？点击左下角的
              <strong className="highlight-text">"继续添加照片"</strong>
              按钮，从相册中选择更多照片就好啦！
            </p>
          </div>
        </div>
      ),
      placement: 'right',
      disableBeacon: true,
      spotlightClicks: true
    },
    {
      target: '.watermarkBatchEditor .rightPanel',
      content: (
        <div className="joyride-step-content">
          <div className="joyride-step-header">
            <div className="joyride-step-number">2</div>
            <h3 className="joyride-step-title">像改文字一样简单 ✍️</h3>
          </div>
          <p className="joyride-step-description">
            在这里，您可以随心所欲地调整水印信息。想要
            <span className="badge-modify">修改</span>项目名称、
            <span className="badge-add">添加</span>自定义项，或者
            <span className="badge-delete">删除</span>
            不需要的内容？直接在这里改就好。
          </p>
          <ul
            className="joyride-step-list"
            style={{ color: '#888', fontSize: '12px' }}
          >
            <li>💡 小贴士：为了保证真实性，时间和地点是不能修改的哦～</li>
          </ul>
        </div>
      ),
      placement: 'left',
      disableBeacon: true
    },
    {
      target: '.watermarkEditPanel .actionButtons',
      content: (
        <div className="joyride-step-content">
          <div className="joyride-step-header">
            <div className="joyride-step-number">3</div>
            <h3 className="joyride-step-title">一键批量修改，省时省力 🚀</h3>
          </div>
          <p className="joyride-step-description">
            改好了？点击<strong className="highlight-text">"批量应用"</strong>
            按钮，会打开一个选择面板，您可以选择将修改应用到
            <strong className="highlight-text">选中的照片</strong>或
            <strong className="highlight-text">全部照片</strong>。
          </p>
          <p
            className="joyride-step-description"
            style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}
          >
            💡 如果只想应用到当前这张照片，点击旁边的
            <strong className="highlight-text">"应用到当前照片"</strong>
            按钮就可以啦！
          </p>
        </div>
      ),
      placement: 'top',
      disableBeacon: true
    }
  ]

  const handleJoyrideCallback = useCallback(
    (data: CallBackProps) => {
      const { status, type, action, index } = data

      // 添加/移除高亮类名
      const leftPanel = document.querySelector(
        '.watermarkBatchEditor .leftPanel'
      )
      if (leftPanel) {
        if (index === 0 && (type === 'step:after' || type === 'step:before')) {
          // 第一步激活时，添加高亮类名
          leftPanel.classList.add('joyride-step-add-photo-active')
        } else {
          // 其他步骤时，移除高亮类名
          leftPanel.classList.remove('joyride-step-add-photo-active')
        }
      }

      // 处理关闭按钮点击（action === 'close'）或完成/跳过状态
      if (
        action === ACTIONS.CLOSE ||
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED
      ) {
        // 引导结束时，移除高亮类名
        const leftPanel = document.querySelector(
          '.watermarkBatchEditor .leftPanel'
        )
        if (leftPanel) {
          leftPanel.classList.remove('joyride-step-add-photo-active')
        }
        setStepIndex(0)

        // 保存到本地缓存，标记已显示过
        localStorage.setItem(GUIDE_SHOWN_KEY, 'true')

        // 调用回调
        if (action === ACTIONS.CLOSE || status === STATUS.SKIPPED) {
          // 关闭按钮或跳过按钮
          if (onSkip) {
            onSkip()
          }
        } else if (status === STATUS.FINISHED && onFinish) {
          // 完成引导
          onFinish()
        }

        if (onComplete) {
          onComplete()
        }
      } else if (type === 'step:after') {
        if (action === 'prev') {
          setStepIndex(data.index - 1)
        } else if (action === 'next') {
          setStepIndex(data.index + 1)
        }
      } else if (type === 'target:notFound') {
        // 如果目标元素未找到，跳到下一步
        setStepIndex(data.index + 1)
      }
    },
    [onComplete, onSkip, onFinish]
  )

  // 重置步骤索引当 run 改变时
  useEffect(() => {
    if (run) {
      setStepIndex(0)
      // 添加作用域类名到 body，用于限制 react-joyride 样式作用域
      document.body.classList.add('watermark-batch-editor-guide-active')
      // 添加高亮类名
      const leftPanel = document.querySelector(
        '.watermarkBatchEditor .leftPanel'
      )
      if (leftPanel) {
        leftPanel.classList.add('joyride-step-add-photo-active')
      }
    } else {
      // 移除作用域类名
      document.body.classList.remove('watermark-batch-editor-guide-active')
      // 移除高亮类名
      const leftPanel = document.querySelector(
        '.watermarkBatchEditor .leftPanel'
      )
      if (leftPanel) {
        leftPanel.classList.remove('joyride-step-add-photo-active')
      }
    }
    // 清理函数：组件卸载时移除类名
    return () => {
      document.body.classList.remove('watermark-batch-editor-guide-active')
      const leftPanel = document.querySelector(
        '.watermarkBatchEditor .leftPanel'
      )
      if (leftPanel) {
        leftPanel.classList.remove('joyride-step-add-photo-active')
      }
    }
  }, [run])

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      scrollToFirstStep={true}
      disableOverlayClose={true}
      disableScrollParentFix={false}
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: '#1890ff',
          zIndex: 10000,
          arrowColor: '#ffffff'
        },
        tooltip: {
          borderRadius: '8px',
          padding: 0,
          maxWidth: '400px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          border: '1px solid #e8e8e8',
          overflow: 'visible'
        },
        tooltipFooter: {
          padding: '16px 20px 20px 20px'
        },
        tooltipContainer: {
          textAlign: 'left'
        },
        tooltipTitle: {
          fontSize: '0'
        },
        buttonNext: {
          backgroundColor: '#1890ff',
          borderRadius: '4px',
          padding: '6px 16px',
          fontSize: '14px',
          fontWeight: 400,
          border: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease'
        },
        buttonBack: {
          color: '#595959',
          marginRight: '8px',
          fontSize: '14px',
          padding: '6px 16px',
          border: '1px solid #d9d9d9',
          borderRadius: '4px',
          backgroundColor: '#ffffff',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        },
        buttonSkip: {
          color: '#8c8c8c',
          fontSize: '14px',
          padding: '6px 16px',
          cursor: 'pointer',
          transition: 'color 0.2s ease'
        },
        overlay: {
          backgroundColor: 'rgba(0, 0, 0, 0.45)'
        },
        spotlight: {
          borderRadius: '4px'
        },
        beacon: {
          display: 'none'
        },
        beaconInner: {
          display: 'none'
        },
        beaconOuter: {
          display: 'none'
        }
      }}
      locale={{
        back: '上一步',
        close: '关闭',
        last: '知道了',
        next: '下一步',
        skip: '跳过引导'
      }}
    />
  )
}

export default UsageGuide
